import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, switchMap, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { CryptoService } from './crypto.service';

export interface AuthUser {
  id: string;
  email: string;
  role: string;
  fullName: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly cryptoService = inject(CryptoService);
  private get baseUrl(): string { return `${this.config.apiEndPoint}admin/auth`; }

  private readonly _currentUser = signal<AuthUser | null>(
    JSON.parse(localStorage.getItem('krc_user') ?? 'null'),
  );

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => !!localStorage.getItem('krc_token'));

  login(email: string, password: string) {
    return from(this.cryptoService.encrypt({ email, password })).pipe(
      switchMap(envelope =>
        this.http.post<any>(`${this.baseUrl}/login`, JSON.stringify(envelope), {
          headers: { 'Content-Type': 'application/json' },
        }),
      ),
      tap(res => {
        localStorage.setItem('krc_token', res.data.token);
        localStorage.setItem('krc_refresh_token', res.data.refreshToken);
        localStorage.setItem('krc_user', JSON.stringify(res.data.user));
        this._currentUser.set(res.data.user);
      }),
    );
  }

  logout() {
    return this.http.post(`${this.baseUrl}/logout`, {}).pipe(
      tap(() => {
        localStorage.removeItem('krc_token');
        localStorage.removeItem('krc_refresh_token');
        localStorage.removeItem('krc_user');
        this._currentUser.set(null);
      }),
    );
  }

  getToken(): string | null {
    return localStorage.getItem('krc_token');
  }

  // ── Homeowner session token (X-Session-Token) ─────────────────────────────
  getSessionToken(): string | null {
    return localStorage.getItem('krc_session_token');
  }

  setSessionToken(token: string): void {
    localStorage.setItem('krc_session_token', token);
  }

  clearSessionToken(): void {
    localStorage.removeItem('krc_session_token');
  }
}
